import { API_BASE, apiSlice } from '@/redux/api-slice'
import type { AppDispatch } from '@/redux/store'
import { mapServerToState, mapToQuery } from './maps/searchState'
import type {
  SavedSearch,
  SearchFilterState,
  SearchPayload,
  SearchQueryPayload,
  SearchQueryResult,
  SelectFilterOptions,
} from './types'

/** Body sent to POST search/Export — mirrors the C# SearchRequest model. */
export type SearchExportBody = SearchQueryPayload

const url = API_BASE + 'search'

const onNewSavedSearchStarted = async (
  arg: NewSavedSearchRequest,
  dispatch: AppDispatch,
  queryFulfilled: Promise<{ data: SavedSearch }>,
) => {
  try {
    const { data } = await queryFulfilled
    const savedSearch = {
      ...data,
      name: data.name || arg.name,
      criteria: data.criteria || arg.criteria,
    }

    dispatch(
      searchApi.util.updateQueryData('savedSearch', undefined, (draft) => {
        const existingIndex = draft.findIndex(
          (item) => item.code === savedSearch.code,
        )

        if (existingIndex >= 0) {
          draft[existingIndex] = savedSearch
        } else {
          draft.unshift(savedSearch)
        }
      }),
    )
  } catch {
    // no-op: preserve existing invalidation behavior
  }
}

export const searchApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: ['SearchResult', 'SavedSearch', 'SearchSettings'],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      //Media Search
      search: build.query<SearchQueryResult, SearchPayload | null>({
        query: (searchPayload) => ({
          method: 'post',
          url: `${url}`,
          body: searchPayload ? mapToQuery(searchPayload) : null,
        }),
        providesTags: ['Search'],
      }),

      searchRecent: build.query<SearchQueryResult, RecentSearchQuery>({
        query: ({ skip = 0, take = 25 }) => ({
          method: 'post',
          url: `${url}/RecentlyViewed`,
          body: { skip, take },
        }),
      }),

      filterOptions: build.query<SelectFilterOptions, void>({
        query: () => ({
          method: 'get',
          url: `${url}/filterOptions`,
        }),
      }),

      //Saved Searches
      savedSearch: build.query<SavedSearch[], void>({
        query: () => ({
          method: 'get',
          url: `${url}/savedSearch`,
        }),
        providesTags: ['SavedSearch'],
      }),

      parseSearchString: build.query<SearchFilterState, string>({
        query: (savedSearchString) => ({
          method: 'get',
          url: `${url}/SavedSearch/parse`,
          params: { savedSearchString },
        }),
        transformResponse: (
          baseQueryReturnValue: SearchQueryPayload,
          _meta,
          _arg,
        ) => {
          return mapServerToState(baseQueryReturnValue)
        },
      }),

      newSavedSearch: build.mutation<SavedSearch, NewSavedSearchRequest>({
        query: (body) => ({
          method: 'post',
          url: `${url}/SavedSearch/New`,
          body,
        }),
        onQueryStarted(arg, { dispatch, queryFulfilled }) {
          return onNewSavedSearchStarted(arg, dispatch, queryFulfilled)
        },
        invalidatesTags: ['SavedSearch'],
      }),

      deleteSavedSearch: build.mutation<void, number>({
        query: (code) => ({
          method: 'delete',
          url: `${url}/SavedSearch/${code}`,
        }),
        invalidatesTags: ['SavedSearch'],
      }),

      exportSearch: build.mutation<void, SearchExportBody>({
        query: (body) => ({
          method: 'post',
          url: `${url}/Export`,
          body,
        }),
      }),
    }),
  })

type NewSavedSearchRequest = {
  name: string
  criteria: string
}

export type RecentSearchQuery = {
  skip: number
  take: number
}

export default searchApi
export const {
  useSearchQuery,
  useLazySearchQuery,
  useSavedSearchQuery,
  useFilterOptionsQuery,
  useLazySearchRecentQuery,
  useSearchRecentQuery,
  useLazyParseSearchStringQuery,
  useDeleteSavedSearchMutation,
  useNewSavedSearchMutation,
  useExportSearchMutation,
} = searchApi
